import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Nullable } from 'types/global.types';
import { useTranslation } from 'react-i18next';
import { GooglePlacesCacheKeys } from 'types/cacheKeys.types';
import useQuery from 'hooks/react-query/useQuery';

// ---------------------------------------------------------------------------
// Google Places API (New) REST response types
// https://developers.google.com/maps/documentation/places/web-service/reference/rest/v1/places/autocomplete
// ---------------------------------------------------------------------------

type FormattableText = {
  text: string;
};

type PlacePrediction = {
  placeId: string;
  text: FormattableText;
  structuredFormat: {
    mainText: FormattableText;
    secondaryText?: FormattableText;
  };
  /**
   * Place types returned by Places Autocomplete (New) — e.g. `street_address`,
   * `route`, `premise`, `geocode`. Used to decide if the pick is a precise
   * address (has house number) without a Place Details round-trip.
   * @see https://developers.google.com/maps/documentation/places/web-service/place-types
   */
  types?: string[];
};

type AutocompleteSuggestion = {
  placePrediction?: PlacePrediction;
};

type AutocompleteResponse = {
  suggestions?: AutocompleteSuggestion[];
};

type AddressComponent = {
  longText: string;
  shortText: string;
  types: string[];
};

type PlaceDetailsResponse = {
  addressComponents?: AddressComponent[];
};

// ---------------------------------------------------------------------------
// Hook public types
// ---------------------------------------------------------------------------

export type AddressResult = {
  street: string;
  city: string;
  zip: string;
  country: string;
  state?: string;
  hasStreetNumber: boolean;
};

export type PlaceSuggestion = {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText?: string;
  /** Place types from the Autocomplete response (e.g. `["street_address", "geocode"]`). */
  types: string[];
};

/**
 * Autocomplete primary types that indicate a precise address (has house number).
 * Other types like `route`, `locality`, `postal_code` are considered incomplete.
 */
const PRECISE_ADDRESS_TYPES = [
  'street_address',
  'premise',
  'subpremise',
] as const;

/** True when Autocomplete `types` includes a precise street-level place type. */
export const hasPreciseAddressType = (
  types: readonly string[] | undefined,
): boolean =>
  !!types?.some((t) =>
    (PRECISE_ADDRESS_TYPES as readonly string[]).includes(t),
  );

/**
 * Primary types accepted by Places Autocomplete (New) `includedPrimaryTypes`
 * (special filters, Table A, and Table B entries allowed for this parameter).
 * At most five values per request.
 *
 * @see https://developers.google.com/maps/documentation/places/web-service/place-types
 * @see https://developers.google.com/maps/documentation/places/web-service/reference/rest/v1/places/autocomplete
 */
export type PlacesAutocompleteIncludedPrimaryType =
  | '(cities)'
  | '(regions)'
  | 'administrative_area_level_1'
  | 'administrative_area_level_2'
  | 'administrative_area_level_3'
  | 'country'
  | 'locality'
  | 'postal_code'
  | 'school_district'
  | 'geocode'
  | 'intersection'
  | 'neighborhood'
  | 'plus_code'
  | 'postal_code_prefix'
  | 'postal_code_suffix'
  | 'postal_town'
  | 'premise'
  | 'route'
  | 'street_address'
  | 'subpremise'
  | 'sublocality'
  | 'sublocality_level_1';

export type UseGoogleAddressAutocompleteOptions = {
  onPlaceSelect?: (address: AddressResult) => void;
  /**
   * Extra listbox rows after Places suggestions (e.g. 1 for “enter manually”).
   * Enables keyboard navigation and Enter when there are zero API suggestions.
   */
  trailingListboxOptionCount?: number;
  /** Called when the user activates the trailing row (see `trailingListboxOptionCount`). */
  onTrailingListboxOptionSelect?: () => void;
  countryRestrictions?: string[];
  /**
   * Restrict autocomplete to Places whose primary type is in this list (Places API New).
   * E.g. `["postal_code"]` for postcode-only lookup (avoids street-address matches).
   * @see https://developers.google.com/maps/documentation/places/web-service/place-types
   */
  includedPrimaryTypes?: readonly PlacesAutocompleteIncludedPrimaryType[];
  /**
   * When true, a pick whose Autocomplete `types` lacks a precise address signal
   * (`street_address | premise | subpremise`) is reported with
   * `hasStreetNumber: false` **without** a Place Details round-trip — letting
   * the consumer block forward progress for incomplete addresses.
   *
   * Leave `false` (default) for non-street lookups like postcodes or cities,
   * where `types` never includes `street_address`.
   */
  requirePreciseAddress?: boolean;
  /**
   * Optional location context prepended to the search query to bias results
   * toward a known area. Pass all available context (zip, city, state) as an
   * array — falsy values are filtered out automatically. The most specific
   * signal (zip) disambiguates when multiple places share the same city name.
   *
   * @example ["80331", "München", "Bayern", undefined]
   */
  locationHint?: Array<Nullable<string>>;
};

export type UseGoogleAddressAutocompleteReturn = {
  suggestions: PlaceSuggestion[];
  isOpen: boolean;
  activeIndex: number;
  handleInputChange: (value: string) => void;
  handleSuggestionSelect: (suggestion: PlaceSuggestion) => Promise<void>;
  handleKeyDown: (e: KeyboardEvent) => void;
  closeDropdown: () => void;
};

// ---------------------------------------------------------------------------
// Constants & fetch helper
// ---------------------------------------------------------------------------

const AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const PLACE_DETAILS_URL = 'https://places.googleapis.com/v1/places';
const SUGGESTIONS_STALE_TIME = 5 * 60 * 1000;

/**
 * Standalone fetch function used as the react-query `queryFn`.
 * Uses the `signal` from TanStack Query v5's query context so in-flight
 * requests abort when the query key changes, the query is cancelled, or
 * observers unmount (see https://tanstack.com/query/latest/docs/framework/react/guides/query-cancellation).
 */
const fetchSuggestions = (
  searchInput: string,
  countryRestrictions: string[] | undefined,
  includedPrimaryTypes:
    | readonly PlacesAutocompleteIncludedPrimaryType[]
    | undefined,
  apiKey: string,
  languageCode: string,
  signal: AbortSignal,
): Promise<PlaceSuggestion[]> => {
  const body: Record<string, unknown> = {
    input: searchInput,
    languageCode,
  };
  if (countryRestrictions?.length) {
    body.includedRegionCodes = countryRestrictions.map((c) => c.toUpperCase());
  }
  if (includedPrimaryTypes?.length) {
    body.includedPrimaryTypes = [...includedPrimaryTypes];
  }

  return fetch(AUTOCOMPLETE_URL, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
    },
    body: JSON.stringify(body),
  })
    .then((response) => {
      if (!response.ok)
        throw new Error(`Places autocomplete failed: ${response.status}`);
      return response.json() as Promise<AutocompleteResponse>;
    })
    .then((data) =>
      (data.suggestions ?? [])
        .filter(
          (s): s is Required<AutocompleteSuggestion> => !!s.placePrediction,
        )
        .map((s) => ({
          placeId: s.placePrediction.placeId,
          description: s.placePrediction.text.text,
          mainText: s.placePrediction.structuredFormat.mainText.text,
          secondaryText: s.placePrediction.structuredFormat.secondaryText?.text,
          types: s.placePrediction.types ?? [],
        })),
    );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useGoogleAddressAutocomplete = ({
  onPlaceSelect,
  trailingListboxOptionCount = 0,
  onTrailingListboxOptionSelect,
  countryRestrictions,
  includedPrimaryTypes,
  requirePreciseAddress = false,
  locationHint,
}: UseGoogleAddressAutocompleteOptions): UseGoogleAddressAutocompleteReturn => {
  const { i18n } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const onPlaceSelectRef = useRef(onPlaceSelect);
  onPlaceSelectRef.current = onPlaceSelect;
  const onTrailingListboxOptionSelectRef = useRef(
    onTrailingListboxOptionSelect,
  );
  onTrailingListboxOptionSelectRef.current = onTrailingListboxOptionSelect;
  const languageRef = useRef(i18n.language);
  languageRef.current = i18n.language;

  const apiKey = import.meta.env.VITE_APP_GOOGLE_PLACES_API_KEY as
    | string
    | undefined;

  // -- Debounce the raw input -----------------------------------------------

  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed.length < 2) {
      setDebouncedValue('');
      return;
    }
    const timer = setTimeout(() => setDebouncedValue(trimmed), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // -- Build the search query -----------------------------------------------

  const hint = locationHint?.filter(Boolean).join(', ') || '';
  const searchInput = debouncedValue
    ? hint
      ? `${hint} ${debouncedValue}`
      : debouncedValue
    : '';

  // -- Autocomplete query ---------------------------------------------------

  const { data: suggestions = [] } = useQuery<PlaceSuggestion[]>(
    [
      GooglePlacesCacheKeys.Autocomplete,
      searchInput,
      countryRestrictions ?? [],
      includedPrimaryTypes ?? [],
    ],
    ({ signal }) => {
      if (!apiKey || !searchInput) return [];
      return fetchSuggestions(
        searchInput,
        countryRestrictions,
        includedPrimaryTypes,
        apiKey,
        languageRef.current,
        signal,
      );
    },
    {
      enabled: searchInput.length >= 2 && !!apiKey,
      staleTime: SUGGESTIONS_STALE_TIME,
      retry: false,
    },
  );

  // -- Handlers -------------------------------------------------------------

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    if (!value || value.length < 2) {
      setIsOpen(false);
      setActiveIndex(-1);
    } else {
      setIsOpen(true);
      setActiveIndex(-1);
    }
  }, []);

  const resetAfterPick = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
    setInputValue('');
    setDebouncedValue('');
  }, []);

  const handleSuggestionSelect = useCallback(
    async (suggestion: PlaceSuggestion) => {
      resetAfterPick();

      // Gate on the Autocomplete `types` array (opt-in via
      // `requirePreciseAddress`): if the selection is a `route`/`geocode`
      // without `street_address|premise|subpremise`, skip the Place Details
      // call entirely and let the consumer block forward progress (prefill
      // search only, no form fill, no screen change).
      // Disabled by default so non-street lookups (postcodes, cities) still
      // fetch full Place Details and populate their fields normally.
      if (requirePreciseAddress && !hasPreciseAddressType(suggestion.types)) {
        onPlaceSelectRef.current?.({
          street: suggestion.mainText,
          city: '',
          zip: '',
          country: '',
          hasStreetNumber: false,
        });
        return;
      }

      if (!apiKey) return;

      try {
        const response = await fetch(
          `${PLACE_DETAILS_URL}/${suggestion.placeId}?languageCode=${languageRef.current}`,
          {
            headers: {
              'X-Goog-Api-Key': apiKey,
              'X-Goog-FieldMask': 'addressComponents',
            },
          },
        );

        if (!response.ok) {
          console.error('Place details request failed', response.status);
          return;
        }

        const place: PlaceDetailsResponse = await response.json();
        const components = place.addressComponents ?? [];

        let streetNumber = '';
        let route = '';
        let zip = '';
        let country = '';
        let state = '';

        for (const c of components) {
          if (c.types.includes('street_number')) streetNumber = c.longText;
          if (c.types.includes('route')) route = c.longText;
          if (c.types.includes('postal_code')) zip = c.longText;
          if (c.types.includes('country')) country = c.shortText;
          if (c.types.includes('administrative_area_level_1'))
            state = c.longText;
        }

        const cityTypePriority = [
          'locality',
          'postal_town',
          'sublocality_level_1',
          'administrative_area_level_2',
          'administrative_area_level_1',
        ] as const;
        let city = '';
        for (const type of cityTypePriority) {
          const found = components.find((c) => c.types.includes(type));
          if (found) {
            city = found.longText;
            break;
          }
        }

        const street = route
          ? streetNumber
            ? `${route} ${streetNumber}`
            : route
          : '';

        onPlaceSelectRef.current?.({
          street,
          city,
          zip,
          country,
          state,
          hasStreetNumber: !!streetNumber,
        });
      } catch (err) {
        console.error('Failed to fetch place details', err);
      }
    },
    [apiKey, resetAfterPick, requirePreciseAddress],
  );

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const totalItems = suggestions.length + trailingListboxOptionCount;
      if (!isOpen || totalItems === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
          break;
        case 'Enter':
          if (activeIndex >= 0 && activeIndex < suggestions.length) {
            e.preventDefault();
            void handleSuggestionSelect(suggestions[activeIndex]);
          } else if (
            trailingListboxOptionCount > 0 &&
            activeIndex === suggestions.length
          ) {
            e.preventDefault();
            resetAfterPick();
            onTrailingListboxOptionSelectRef.current?.();
          }
          break;
        case 'Escape':
          e.preventDefault();
          closeDropdown();
          break;
      }
    },
    [
      isOpen,
      suggestions,
      activeIndex,
      trailingListboxOptionCount,
      handleSuggestionSelect,
      closeDropdown,
      resetAfterPick,
    ],
  );

  return {
    suggestions,
    isOpen,
    activeIndex,
    handleInputChange,
    handleSuggestionSelect,
    handleKeyDown,
    closeDropdown,
  };
};
